import React, { useState, useEffect } from 'react';
import { tradingAPI } from '../../../../services/tradingAPI';
import '../../../../css/trading.css';

const Settings = () => {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Form state for thresholds
  const [thresholds, setThresholds] = useState({
    pe_ratio_max: 15.0,
    pb_ratio_max: 2.0,
    debt_to_equity_max: 0.5,
    roe_min: 20.0,
    stop_loss_pct: -15.0,
    trailing_stop_pct: -10.0,
    max_positions: 20,
  });

  useEffect(() => {
    fetchAccount();
  }, []);

  const fetchAccount = async () => {
    try {
      const response = await tradingAPI.getAccount();
      setAccount(response.data);

      // Update form with current values
      setThresholds({
        pe_ratio_max: parseFloat(response.data.pe_ratio_max) || 15.0,
        pb_ratio_max: parseFloat(response.data.pb_ratio_max) || 2.0,
        debt_to_equity_max: parseFloat(response.data.debt_to_equity_max) || 0.5,
        roe_min: parseFloat(response.data.roe_min) || 20.0,
        stop_loss_pct: parseFloat(response.data.stop_loss_pct) || -15.0,
        trailing_stop_pct: parseFloat(response.data.trailing_stop_pct) || -10.0,
        max_positions: parseInt(response.data.max_positions) || 20,
      });

      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load account settings');
      console.error('Account error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSliderChange = (field, value) => {
    setThresholds({ ...thresholds, [field]: parseFloat(value) });
    setSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await tradingAPI.updateAccount(thresholds);
      setSuccess(true);
      fetchAccount(); // Refresh data
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save settings');
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setThresholds({
      pe_ratio_max: 15.0,
      pb_ratio_max: 2.0,
      debt_to_equity_max: 0.5,
      roe_min: 20.0,
      stop_loss_pct: -15.0,
      trailing_stop_pct: -10.0,
      max_positions: 20,
    });
    setSuccess(false);
  };

  if (loading) {
    return <div className="loading">Loading settings...</div>;
  }

  return (
    <div className="settings-tab">
      <div className="settings-header">
        <h2>Trading Strategy Settings</h2>
        <p>Adjust the threshold criteria for your automated trading strategy</p>
      </div>

      {success && (
        <div className="success-message">
          Settings saved successfully! Changes will apply to the next monthly rebalance.
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <div className="settings-content">
        {/* Value Thresholds */}
        <div className="settings-section">
          <h3>Value Thresholds</h3>
          <p className="section-description">
            Stocks must meet ALL value thresholds to qualify for your portfolio
          </p>

          {/* P/E Ratio */}
          <div className="threshold-slider">
            <div className="slider-header">
              <label>Maximum P/E Ratio</label>
              <span className="slider-value">{thresholds.pe_ratio_max.toFixed(1)}</span>
            </div>
            <input
              type="range"
              min="5"
              max="30"
              step="0.5"
              value={thresholds.pe_ratio_max}
              onChange={(e) => handleSliderChange('pe_ratio_max', e.target.value)}
              className="slider"
            />
            <div className="slider-labels">
              <span>5.0 (Very Conservative)</span>
              <span>30.0 (Aggressive)</span>
            </div>
          </div>

          {/* P/B Ratio */}
          <div className="threshold-slider">
            <div className="slider-header">
              <label>Maximum P/B Ratio</label>
              <span className="slider-value">{thresholds.pb_ratio_max.toFixed(1)}</span>
            </div>
            <input
              type="range"
              min="0.5"
              max="5.0"
              step="0.1"
              value={thresholds.pb_ratio_max}
              onChange={(e) => handleSliderChange('pb_ratio_max', e.target.value)}
              className="slider"
            />
            <div className="slider-labels">
              <span>0.5 (Very Conservative)</span>
              <span>5.0 (Aggressive)</span>
            </div>
          </div>

          {/* Debt/Equity */}
          <div className="threshold-slider">
            <div className="slider-header">
              <label>Maximum Debt/Equity Ratio</label>
              <span className="slider-value">{thresholds.debt_to_equity_max.toFixed(2)}</span>
            </div>
            <input
              type="range"
              min="0.1"
              max="2.0"
              step="0.05"
              value={thresholds.debt_to_equity_max}
              onChange={(e) => handleSliderChange('debt_to_equity_max', e.target.value)}
              className="slider"
            />
            <div className="slider-labels">
              <span>0.1 (Very Conservative)</span>
              <span>2.0 (Aggressive)</span>
            </div>
          </div>

          {/* ROE */}
          <div className="threshold-slider">
            <div className="slider-header">
              <label>Minimum ROE (%)</label>
              <span className="slider-value">{thresholds.roe_min.toFixed(1)}%</span>
            </div>
            <input
              type="range"
              min="5"
              max="40"
              step="1"
              value={thresholds.roe_min}
              onChange={(e) => handleSliderChange('roe_min', e.target.value)}
              className="slider"
            />
            <div className="slider-labels">
              <span>5% (Lenient)</span>
              <span>40% (Very Strict)</span>
            </div>
          </div>
        </div>

        {/* Risk Management */}
        <div className="settings-section">
          <h3>Risk Management</h3>
          <p className="section-description">
            Control when positions are automatically sold to protect your capital
          </p>

          {/* Stop Loss */}
          <div className="threshold-slider">
            <div className="slider-header">
              <label>Stop Loss (%)</label>
              <span className="slider-value">{thresholds.stop_loss_pct.toFixed(1)}%</span>
            </div>
            <input
              type="range"
              min="-30"
              max="-5"
              step="1"
              value={thresholds.stop_loss_pct}
              onChange={(e) => handleSliderChange('stop_loss_pct', e.target.value)}
              className="slider"
            />
            <div className="slider-labels">
              <span>-30% (Loose)</span>
              <span>-5% (Tight)</span>
            </div>
          </div>

          {/* Trailing Stop */}
          <div className="threshold-slider">
            <div className="slider-header">
              <label>Trailing Stop (%)</label>
              <span className="slider-value">{thresholds.trailing_stop_pct.toFixed(1)}%</span>
            </div>
            <input
              type="range"
              min="-25"
              max="-5"
              step="1"
              value={thresholds.trailing_stop_pct}
              onChange={(e) => handleSliderChange('trailing_stop_pct', e.target.value)}
              className="slider"
            />
            <div className="slider-labels">
              <span>-25% (Loose)</span>
              <span>-5% (Tight)</span>
            </div>
          </div>
        </div>

        {/* Portfolio Settings */}
        <div className="settings-section">
          <h3>Portfolio Settings</h3>
          <p className="section-description">
            Configure how many positions your portfolio can hold
          </p>

          {/* Max Positions */}
          <div className="threshold-slider">
            <div className="slider-header">
              <label>Maximum Positions</label>
              <span className="slider-value">{thresholds.max_positions}</span>
            </div>
            <input
              type="range"
              min="5"
              max="30"
              step="1"
              value={thresholds.max_positions}
              onChange={(e) => handleSliderChange('max_positions', e.target.value)}
              className="slider"
            />
            <div className="slider-labels">
              <span>5 (Concentrated)</span>
              <span>30 (Diversified)</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="settings-actions">
          <button onClick={resetToDefaults} className="btn btn-secondary">
            Reset to Defaults
          </button>
          <button onClick={handleSave} className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

        {/* Warning */}
        <div className="settings-warning">
          <strong>Note:</strong> Changes to these settings will be applied during the next monthly
          rebalance. Existing positions will follow the current settings until then.
        </div>
      </div>
    </div>
  );
};

export default Settings;
